import { apiSlice } from 'src/features/api/api-slice';
import { ApiResponse } from 'src/common/interfaces';
import {
  CreateTagRequest,
  GetTagsRequest,
  GetTagsResponse,
  Tag,
  TagCategory,
  UpdateTagCategoryRequest,
  UpdateTagRequest,
} from 'src/features/admin/tagging/tagging.types';
import { RTKQueryTag } from 'src/common/constants';

export const taggingApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    createTag: builder.mutation<Tag | undefined, CreateTagRequest>({
      query: (body) => ({
        method: 'POST',
        url: '/tagging/tags',
        body,
      }),
      invalidatesTags: [RTKQueryTag.Tags],
      transformResponse: (response: ApiResponse<Tag>) => response.data,
    }),
    createTagCategory: builder.mutation<TagCategory | undefined, Omit<TagCategory, '_id' | 'createdBy'>>({
      query: (body) => ({
        method: 'POST',
        url: '/tagging/tag-categories',
        body,
      }),
      invalidatesTags: [RTKQueryTag.TagCategories],
      transformResponse: (response: ApiResponse<TagCategory>) => response.data,
    }),
    deleteTag: builder.mutation<ApiResponse, string>({
      query: (tagId) => ({
        method: 'DELETE',
        url: '/tagging/tags',
        params: {
          tagId,
        },
      }),
      invalidatesTags: [RTKQueryTag.Tags],
    }),
    deleteTagCategory: builder.mutation<ApiResponse, string>({
      query: (tagCategoryId) => ({
        method: 'DELETE',
        url: '/tagging/tag-categories',
        params: {
          tagCategoryId,
        },
      }),
      invalidatesTags: [RTKQueryTag.TagCategories],
    }),
    getTagCategories: builder.query<TagCategory[], null>({
      query: () => ({
        url: `/tagging/tag-categories`,
        method: 'GET',
      }),
      providesTags: [RTKQueryTag.TagCategories],
      transformResponse: (response: ApiResponse<TagCategory[]>) => response.data || [],
    }),
    getTags: builder.query<GetTagsResponse, GetTagsRequest>({
      query: (params) => ({
        url: `/tagging/tags`,
        method: 'GET',
        params,
      }),
      providesTags: [RTKQueryTag.Tags],
      transformResponse: (response: ApiResponse<GetTagsResponse>) => response.data || { tags: [], count: 0 },
    }),
    updateTag: builder.mutation<ApiResponse, UpdateTagRequest>({
      query: (request) => ({
        method: 'PUT',
        url: '/tagging/tags',
        body: request.body,
        params: {
          tagId: request.tagId,
        },
      }),
      invalidatesTags: [RTKQueryTag.Tags],
    }),
    updateTagCategory: builder.mutation<ApiResponse, UpdateTagCategoryRequest>({
      query: (request) => ({
        method: 'PUT',
        url: '/tagging/tag-categories',
        body: request.body,
        params: {
          tagCategoryId: request.tagCategoryId,
        },
      }),
    }),
  }),
});

export const {
  useGetTagsQuery,
  useLazyGetTagsQuery,
  useGetTagCategoriesQuery,
  useCreateTagMutation,
  useCreateTagCategoryMutation,
  useDeleteTagMutation,
  useDeleteTagCategoryMutation,
  useUpdateTagMutation,
  useUpdateTagCategoryMutation,
} = taggingApiSlice;
